import React, { useState, useEffect } from 'react';
import StatsCardsDashboard from '../../../components/shared/StatsCardsDashboard';
import savingsService from '../../../services/savingsService';
import {
  Savings,
  TrendingUp,
  BarChart,
  AccountBalance
} from '@mui/icons-material';

/**
 * SavingsStatsDashboard Component
 * 
 * Displays key savings statistics at the top of the savings section
 */
const SavingsStatsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [savingsStats, setSavingsStats] = useState({
    totalSavings: 0,
    monthlySavingsRate: 0,
    annualReturnRate: 0,
    emergencyFundRatio: 0,
    goalProgress: 0,
    savingsToIncomeRatio: 0,
    projectedAnnualGrowth: 0,
    ytdContributions: 0
  });

  useEffect(() => {
    const fetchSavingsStats = async () => {
      try {
        setLoading(true);
        // Fetch actual savings statistics using the service
        const stats = await savingsService.getSavingsStatistics();
        setSavingsStats(stats);
      } catch (error) {
        console.error('Error fetching savings statistics:', error);
        // Handle error state, using default values as fallback
      } finally {
        setLoading(false);
      }
    };

    fetchSavingsStats();
  }, []);

  const formatCurrency = (value) => {
    // Handle cases where value is undefined, null, or not a number
    if (value === undefined || value === null || isNaN(value)) {
      return '$0.00';
    }
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercentage = (value) => {
    // Handle cases where value is undefined, null, or not a number
    if (value === undefined || value === null || isNaN(value)) {
      return '0.0%';
    }
    return `${value.toFixed(1)}%`;
  };

  const savingsMetrics = [
    {
      title: 'Total Savings',
      value: formatCurrency(savingsStats.totalSavings),
      description: `${formatPercentage(savingsStats.goalProgress)} of goal`,
      icon: Savings,
      color: 'emerald'
    },
    {
      title: 'Monthly Rate',
      value: formatCurrency(savingsStats.monthlySavingsRate),
      description: `${formatPercentage(savingsStats.savingsToIncomeRatio * 100)} of income`,
      icon: TrendingUp,
      color: 'green'
    },
    {
      title: 'Annual Return',
      value: formatPercentage(savingsStats.annualReturnRate),
      description: `${formatCurrency(savingsStats.projectedAnnualGrowth)} projected growth`,
      icon: BarChart,
      color: 'purple'
    },
    {
      title: 'Emergency Fund',
      value: `${savingsStats.emergencyFundRatio.toFixed(1)}x`,
      description: 'Months of expenses covered',
      change: `${formatCurrency(savingsStats.ytdContributions)} YTD contributions`,
      isPositive: true,
      icon: AccountBalance,
      color: 'blue'
    }
  ];

  return (
    <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 pt-6">
      <StatsCardsDashboard
        title="Savings"
        metrics={savingsMetrics}
        loading={loading}
      />
    </div>
  );
};

export default SavingsStatsDashboard; 